import { useState } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";

export const ProductCard = ({ product, index = 0, large = false }) => {
  const { addItem, setIsOpen } = useCart();
  const [spot, setSpot] = useState({ x: 50, y: 50 });

  const add = () => {
    addItem(product);
    toast.success(`${product.name} added`, {
      description: `$${product.price.toFixed(2)} / ${product.unit}`,
      action: { label: "View Basket", onClick: () => setIsOpen(true) },
    });
  };

  const onSpot = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setSpot({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
    });
  };

  return (
    <motion.article
      data-testid={`product-card-${product.id}`}
      onMouseMove={onSpot}
      initial={{ opacity: 0, y: 32, scale: 0.97 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative overflow-hidden border border-line bg-surface hover:border-saffron/40 hover:shadow-[0_10px_40px_-10px_rgba(212,141,59,0.15)] transition-[border-color,box-shadow] duration-500 ${
        large ? "md:col-span-2" : ""
      }`}
    >
      <div
        className="pointer-events-none absolute inset-0 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ background: `radial-gradient(420px circle at ${spot.x}% ${spot.y}%, rgba(212,141,59,0.16), transparent 60%)` }}
      />
      {product.availability === "limited" && (
        <span
          data-testid={`badge-limited-${product.id}`}
          className="absolute top-8 left-8 z-20 rounded-full bg-wine border border-wine-light px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-100"
        >
          Limited
        </span>
      )}
      {product.availability === "sold_out" && (
        <span
          data-testid={`badge-sold-out-${product.id}`}
          className="absolute top-8 left-8 z-20 rounded-full bg-zinc-800 border border-line px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400"
        >
          Sold Out
        </span>
      )}
      <div className="p-5 pb-0">
        <div className={`overflow-hidden rounded-t-[10rem] border border-line ${large ? "max-h-[52vh]" : "max-h-[44vh]"}`}>
          <img
            src={product.image}
            alt={product.name}
            loading="lazy"
            className="w-full h-full min-h-[280px] object-cover transition-transform duration-700 group-hover:scale-105"
          />
        </div>
      </div>
      <div className="p-8 pt-6 flex flex-col gap-3">
        <span className="overline-tag text-[10px]">{product.tag}</span>
        <h3 className="font-display text-2xl sm:text-3xl tracking-tight text-zinc-100">{product.name}</h3>
        <p className="text-sm text-zinc-400 leading-relaxed">{product.description}</p>
        <div className="mt-4 flex items-center justify-between">
          <p className="text-zinc-100">
            <span className="font-display text-2xl text-saffron">${product.price.toFixed(2)}</span>
            <span className="text-xs text-zinc-500 ml-1">/ {product.unit}</span>
          </p>
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={add}
            disabled={product.availability === "sold_out"}
            className="flex items-center gap-2 rounded-full border border-saffron/50 px-5 py-2.5 text-xs uppercase tracking-[0.15em] font-bold text-saffron hover:bg-saffron hover:text-obsidian transition-colors duration-300 focus-ring disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-saffron"
          >
            {product.availability === "sold_out" ? (
              "Sold Out"
            ) : (
              <>
                <Plus className="w-3.5 h-3.5" />
                Add
              </>
            )}
          </button>
        </div>
      </div>
    </motion.article>
  );
};
